import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet, agmsClientRequestPost } from "../../apis/request";
import Loading from "../../assets/uxnicon/loadingIcon.png";
import AdminLayout from "./AdminLayout";
import { formatServerTime } from "../../utils/serverTime";

const PAGE_SIZE = 20;

// device_type 코드 → 기기 이름 (내부탐색 기기타입과 동일 체계, 1234만 개인용 원본이라 미분류)
const DEVICE_TYPE_NAME = {
  "1101": "T01", "1102": "T10", "1103": "T10-2",
  "1104": "F21", "1105": "F23", "1106": "I10", "1107": "E10", "1108": "F25",
  "1234": "미분류",
};
const deviceTypeName = (code) => DEVICE_TYPE_NAME[String(code)] ?? (code ? String(code) : "-");

const thBase = {
  backgroundColor: "#787397",
  color: "#fff",
  fontWeight: "bold",
  fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px",
  borderRight: "1px solid #EBEBEB",
  textAlign: "center",
};

const tdBase = {
  backgroundColor: "#fff",
  textAlign: "center",
  borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB",
  padding: "8px 12px",
};

const pageBtn = (active, disabled) => ({
  minWidth: "34px",
  padding: "6px 10px",
  border: active ? "1px solid #787397" : "1px solid #d5d0dd",
  borderRadius: "6px",
  background: active ? "#787397" : "#fff",
  color: active ? "#fff" : "#5E2589",
  fontWeight: active ? "bold" : "normal",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

const AdminDeviceList = () => {
  const [pageResp, setPageResp] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [hoverIdx, setHoverIdx] = useState(null);
  const [sort, setSort] = useState("recent"); // recent(최근 사용일)/id(등록순)/serial(시리얼)

  // 기기 등록
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ serial_number: "", device_mac: "", device_type: "1108" });
  const [busy, setBusy] = useState(false);

  const callApi = async (p, s, srt) => {
    setError(null);
    try {
      const res = await agmsClientRequestGet(`/api/admin/devices?page=${p}&size=${PAGE_SIZE}&search=${encodeURIComponent(s || "")}&sort=${srt}`);
      if (res && Array.isArray(res.content)) {
        setPageResp(res);
      } else {
        setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
      }
    } catch (e) {
      setError("기기 목록을 불러오지 못했습니다. (권한 또는 서버 오류)");
      setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
    }
  };

  useEffect(() => {
    callApi(page, search, sort);
  }, [page, search, sort]);

  const submitRegister = async () => {
    const serial = (regForm.serial_number || "").trim();
    const mac = (regForm.device_mac || "").trim();
    if (!serial || !mac) { alert("시리얼과 MAC을 입력해주세요."); return; }
    // MAC 형식 검증 (내부탐색과 동일: XX:XX:XX:XX:XX:XX, 대문자/숫자)
    if (!/^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){5}$/.test(mac)) { alert("MAC 형식이 올바르지 않습니다. (예: 60:C0:BF:FC:00:11)"); return; }
    setBusy(true);
    try {
      const res = await agmsClientRequestPost(`/api/admin/devices`, {
        serial_number: serial,
        device_mac: mac.toUpperCase(),
        device_type: regForm.device_type,
      });
      if (res && res.error) { alert("등록 실패: " + res.error); return; }
      setShowRegister(false);
      setRegForm({ serial_number: "", device_mac: "", device_type: "1108" });
      setPage(1);
      await callApi(1, search, sort);
      alert("기기 등록 완료" + (res && res.device_id ? ` (ID ${res.device_id})` : ""));
    } catch (e) {
      alert("등록 중 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  if (pageResp === null) {
    return (
      <AdminLayout>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px" }}>
          <img
            src={Loading}
            alt="loading..."
            style={{ width: "50px", height: "50px", animation: ".75s linear infinite spinner-border" }}
          />
        </div>
      </AdminLayout>
    );
  }

  const content = pageResp.content ?? [];
  const total = pageResp.totalElements ?? 0;
  const totalPages = Math.max(1, pageResp.totalPages ?? 1);
  const currentPage = Math.min(page, totalPages);

  return (
    <AdminLayout>
      <div style={{ width: "100%" }}>
        <div style={{ marginBottom: "17px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: "bold", fontSize: "20px" }}>기기 목록</div>
          <div style={{ color: "#84818A" }}>총 {total}대</div>
        </div>

        {/* 검색 (시리얼·MAC) */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
            placeholder="시리얼 또는 MAC 검색"
            style={{ flex: "0 0 320px", maxWidth: "100%", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px" }}
          />
          <button
            onClick={applySearch}
            style={{ padding: "8px 16px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
          >
            검색
          </button>
          {search && (
            <button
              onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
              style={{ padding: "8px 14px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
            >
              초기화
            </button>
          )}
          {/* 정렬 드롭다운 + 디바이스 등록 (오른쪽, 같은 줄) */}
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            style={{ marginLeft: "auto", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px", background: "#fff", cursor: "pointer" }}
          >
            <option value="recent">최근 사용일순</option>
            <option value="id">등록순(ID)</option>
            <option value="serial">시리얼순</option>
          </select>
          <button
            onClick={() => setShowRegister(true)}
            style={{ padding: "8px 16px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            + 디바이스 등록
          </button>
        </div>

        {error && <div style={{ color: "#d9534f", marginBottom: "12px" }}>{error}</div>}

        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...thBase, borderRadius: "5px 0px 0px 5px", minWidth: "60px" }}>ID</th>
                <th style={{ ...thBase, minWidth: "120px" }}>시리얼</th>
                <th style={{ ...thBase, minWidth: "180px" }}>MAC</th>
                <th style={{ ...thBase, minWidth: "100px" }}>타입</th>
                <th style={{ ...thBase, minWidth: "140px" }}>최근 사용자</th>
                <th style={{ ...thBase, borderRight: "none", borderRadius: "0px 5px 5px 0px", minWidth: "150px" }}>최근 사용일</th>
              </tr>
            </thead>
            <tbody>
              {content.length === 0 ? (
                <tr>
                  <td style={{ ...tdBase, borderRight: "none" }} colSpan={6}>
                    표시할 기기가 없습니다.
                  </td>
                </tr>
              ) : (
                content.map((d, i) => {
                  const cellBg = hoverIdx === i ? "#EFE9F7" : "#fff";
                  const cell = { ...tdBase, backgroundColor: cellBg };
                  const cellLast = { ...tdBase, backgroundColor: cellBg, borderRight: "none" };
                  return (
                    <tr key={d.id ?? i} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
                      <td style={cell}>{d.id}</td>
                      <td style={cell}>{d.serial_number ?? "-"}</td>
                      <td style={cell}>{d.device_mac ?? "-"}</td>
                      <td style={cell}>{deviceTypeName(d.device_type)}</td>
                      <td style={cell}>
                        {d.recent_user_name ? `${d.recent_user_name} (#${d.recent_user_id})` : "-"}
                      </td>
                      <td style={cellLast}>{formatServerTime(d.recent_used_dt, "YYYY-MM-DD HH:mm:ss", "-")}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {content.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "20px" }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={pageBtn(false, currentPage === 1)}>
              이전
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={pageBtn(p === currentPage, false)}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={pageBtn(false, currentPage === totalPages)}>
              다음
            </button>
          </div>
        )}
      </div>

      {/* 디바이스 등록 모달 */}
      {showRegister && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
             onClick={() => !busy && setShowRegister(false)}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "28px", width: "420px", maxWidth: "92vw", boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "18px" }}>디바이스 등록</div>

            <label style={{ display: "block", fontSize: "13px", color: "#84818A", marginBottom: "4px" }}>시리얼</label>
            <input value={regForm.serial_number}
                   onChange={(e) => setRegForm((f) => ({ ...f, serial_number: e.target.value }))}
                   placeholder="시리얼 번호 직접 입력"
                   style={{ width: "100%", padding: "8px 10px", border: "1px solid #CBCBCB", borderRadius: "8px", marginBottom: "14px", boxSizing: "border-box" }} />

            <label style={{ display: "block", fontSize: "13px", color: "#84818A", marginBottom: "4px" }}>MAC</label>
            <input value={regForm.device_mac}
                   onChange={(e) => setRegForm((f) => ({ ...f, device_mac: e.target.value }))}
                   placeholder="예: 60:C0:BF:FC:00:11"
                   style={{ width: "100%", padding: "8px 10px", border: "1px solid #CBCBCB", borderRadius: "8px", marginBottom: "14px", boxSizing: "border-box" }} />

            <label style={{ display: "block", fontSize: "13px", color: "#84818A", marginBottom: "4px" }}>타입</label>
            <select value={regForm.device_type}
                    onChange={(e) => setRegForm((f) => ({ ...f, device_type: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #CBCBCB", borderRadius: "8px", marginBottom: "20px", boxSizing: "border-box" }}>
              {["1101", "1102", "1103", "1104", "1105", "1106", "1107", "1108"].map((c) => (
                <option key={c} value={c}>{deviceTypeName(c)}</option>
              ))}
            </select>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button onClick={() => setShowRegister(false)} disabled={busy}
                      style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}>
                취소
              </button>
              <button onClick={submitRegister} disabled={busy}
                      style={{ padding: "9px 18px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}>
                {busy ? "등록 중..." : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default withRouter(AdminDeviceList);
